import React, { useRef, useState } from 'react'
import { useTranslation } from 'i18n'

import RichText from './RichText'
import * as styles from '../ImageEnhancement.module.scss'

/*
  UploadStage: lets the user pick a photo of themselves and continue.
  Holds the selected File and a local preview URL; hands the File up via onContinue.
*/
const UploadStage = ({ copy, cardClassName, heroImageUrl, initialFile, onContinue }) => {
  const { t } = useTranslation(['imageEnhancement', 'common'])
  const inputRef = useRef(null)
  const [file, setFile] = useState(initialFile || null)
  const [previewUrl, setPreviewUrl] = useState(
    initialFile ? URL.createObjectURL(initialFile) : null,
  )

  const openPicker = () => inputRef.current && inputRef.current.click()

  const handleFile = selected => {
    if (!selected) return
    setFile(selected)
    setPreviewUrl(URL.createObjectURL(selected))
  }

  return (
    <div className={cardClassName}>
      <div className={styles.cardHeader}>
        <RichText as="h2" className={styles.title} html={copy.uploadTitle} />
        <RichText as="p" className={styles.subtitle} html={copy.uploadSubtitle} />
      </div>

      <div className={heroImageUrl ? styles.uploadSideBySide : styles.uploadSingle}>
        {heroImageUrl && (
          <div className={styles.heroBox}>
            <img
              className={styles.resultImage}
              src={heroImageUrl}
              alt={t('imageEnhancement:heroExampleAlt')}
            />
            <span className={styles.heroBadge}>{t('imageEnhancement:heroExampleBadge')}</span>
          </div>
        )}
        {previewUrl ? (
          <div className={styles.imageBox}>
            <img className={styles.resultImage} src={previewUrl} alt={t('imageEnhancement:uploadPreviewAlt')} />
          </div>
        ) : (
          <div
            className={styles.dropzone}
            role="button"
            tabIndex={0}
            onClick={openPicker}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') openPicker()
            }}
          >
            <svg
              className={styles.dropzoneIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 16V4" />
              <path d="M7 9l5-5 5 5" />
              <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
            </svg>
            <span className={styles.dropzoneLabel}>
              {t('imageEnhancement:choosePhoto')}
            </span>
          </div>
        )}
      </div>

      {!previewUrl && (
        <RichText
          as="p"
          className={styles.helpText}
          html={copy.uploadHelpText || t('imageEnhancement:uploadHelpText')}
        />
      )}

      <input
        ref={inputRef}
        className={styles.hiddenInput}
        type="file"
        accept="image/*"
        onChange={e => handleFile(e.target.files && e.target.files[0])}
      />

      {previewUrl && (
        <button type="button" className={styles.btnLink} onClick={openPicker}>
          {t('imageEnhancement:chooseDifferentPhoto')}
        </button>
      )}

      <button
        type="button"
        className={styles.btn}
        disabled={!file}
        onClick={() => onContinue(file)}
      >
        {copy.uploadCtaText || t('common:continue')}
      </button>
    </div>
  )
}

export default UploadStage
